import { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, Users, Banknote, PieChart, Clock, User, Inbox, Target } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { useStore } from '../store/useStore';
import { formatCurrency } from '../lib/utils';
import { effHours, type SalesEntry } from './Sales';
import { QUALITY_CONFIG, STATUS_CONFIG, type Inquiry, type InquiryQuality } from './Inquiries';

// ── Periods ──────────────────────────────────────────────────────────────────

type PeriodKey = 'this_month' | 'last_month' | 'qtd' | 'last_quarter' | 'ytd' | 'last_year' | 't12' | 'custom';

const PERIODS: { key: PeriodKey; label: string; growthLabel: string }[] = [
  { key: 'this_month', label: 'See kuu', growthLabel: 'vs eelmine kuu' },
  { key: 'last_month', label: 'Eelmine kuu', growthLabel: 'vs üle-eelmine kuu' },
  { key: 'qtd', label: 'Kvartal (jooksev)', growthLabel: 'vs eelmine kvartal' },
  { key: 'last_quarter', label: 'Eelmine kvartal', growthLabel: 'vs üle-eelmine kvartal' },
  { key: 'ytd', label: 'Aasta algusest', growthLabel: 'vs sama periood mullu' },
  { key: 'last_year', label: 'Eelmine aasta', growthLabel: 'vs üle-eelmine aasta' },
  { key: 't12', label: 'Jooksev 12 kuud', growthLabel: 'vs eelmised 12 kuud' },
  { key: 'custom', label: 'Kohandatud', growthLabel: 'vs eelnev sama pikk periood' },
];

function mk(y: number, mIdx: number): string {
  const d = new Date(Date.UTC(y, mIdx, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function keyParts(k: string): [number, number] {
  const [y, m] = k.split('-').map(Number);
  return [y, m - 1];
}

function addKey(k: string, delta: number): string {
  const [y, m] = keyParts(k);
  return mk(y, m + delta);
}

function monthsBetween(from: string, to: string): number {
  const [fy, fm] = keyParts(from);
  const [ty, tm] = keyParts(to);
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/** Inclusive month-key ranges for the period and its comparison period. */
function ranges(period: PeriodKey, customFrom: string, customTo: string): { from: string; to: string; prevFrom: string; prevTo: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = m - (m % 3);
  switch (period) {
    case 'this_month':   return { from: mk(y, m), to: mk(y, m), prevFrom: mk(y, m - 1), prevTo: mk(y, m - 1) };
    case 'last_month':   return { from: mk(y, m - 1), to: mk(y, m - 1), prevFrom: mk(y, m - 2), prevTo: mk(y, m - 2) };
    case 'qtd':          return { from: mk(y, q), to: mk(y, m), prevFrom: mk(y, q - 3), prevTo: mk(y, q - 1) };
    case 'last_quarter': return { from: mk(y, q - 3), to: mk(y, q - 1), prevFrom: mk(y, q - 6), prevTo: mk(y, q - 4) };
    case 'ytd':          return { from: mk(y, 0), to: mk(y, m), prevFrom: mk(y - 1, 0), prevTo: mk(y - 1, m) };
    case 'last_year':    return { from: mk(y - 1, 0), to: mk(y - 1, 11), prevFrom: mk(y - 2, 0), prevTo: mk(y - 2, 11) };
    case 't12':          return { from: mk(y, m - 11), to: mk(y, m), prevFrom: mk(y, m - 23), prevTo: mk(y, m - 12) };
    case 'custom': {
      // Compare with the equally long window immediately before the range.
      let from = customFrom, to = customTo;
      if (from > to) [from, to] = [to, from];
      const n = monthsBetween(from, to);
      return { from, to, prevFrom: addKey(from, -n), prevTo: addKey(from, -1) };
    }
  }
}

// ── Aggregation ──────────────────────────────────────────────────────────────

interface Agg {
  revenue: number;
  design: number;
  dev: number;
  hoursDesign: number;
  hoursDev: number;
  hoursTotal: number;
  clientCount: number;
  newClients: number;
  returningClients: number;
  avgDeal: number | null;
  // Per spec: sum per client first, then average across clients.
  avgClientTotal: number | null;
  avgClientDesign: number | null;
  avgClientDev: number | null;
}

function aggregate(entries: SalesEntry[], from: string, to: string): Agg {
  const rows = entries.filter((e) => {
    const k = e.month.split('T')[0].slice(0, 7);
    return k >= from && k <= to;
  });

  const num = (v: number | null) => (v != null ? Number(v) : null);
  const revenue = rows.reduce((s, r) => s + Number(r.total), 0);
  const design = rows.reduce((s, r) => s + Number(r.designAmount ?? 0), 0);
  const dev = rows.reduce((s, r) => s + Number(r.devAmount ?? 0), 0);
  // Hours sold: manual hours when logged, otherwise the € budget / 70.
  const hoursDesign = rows.reduce((s, r) => s + (effHours(num(r.designAmount), num(r.designHours)) ?? 0), 0);
  const hoursDev = rows.reduce((s, r) => s + (effHours(num(r.devAmount), num(r.devHours)) ?? 0), 0);

  const byClient = new Map<string, { total: number; design: number; dev: number }>();
  const newKeys = new Set<string>();
  for (const r of rows) {
    const key = r.clientKey || r.companyName.toLowerCase();
    if (!byClient.has(key)) byClient.set(key, { total: 0, design: 0, dev: 0 });
    const c = byClient.get(key)!;
    c.total += Number(r.total);
    c.design += Number(r.designAmount ?? 0);
    c.dev += Number(r.devAmount ?? 0);
    if (r.isNewClient) newKeys.add(key);
  }

  const clientCount = byClient.size;
  const perClient = Array.from(byClient.values());
  const avg = (f: (c: { total: number; design: number; dev: number }) => number) =>
    clientCount ? perClient.reduce((s, c) => s + f(c), 0) / clientCount : null;

  return {
    revenue,
    design,
    dev,
    hoursDesign,
    hoursDev,
    hoursTotal: hoursDesign + hoursDev,
    clientCount,
    newClients: newKeys.size,
    returningClients: clientCount - newKeys.size,
    avgDeal: clientCount ? revenue / clientCount : null,
    avgClientTotal: avg((c) => c.total),
    avgClientDesign: avg((c) => c.design),
    avgClientDev: avg((c) => c.dev),
  };
}

function growthPct(cur: number, prev: number): number | null {
  if (!prev) return null;
  return ((cur - prev) / prev) * 100;
}

function GrowthChip({ pct, label }: { pct: number | null; label: string }) {
  if (pct == null) return <span className="text-xs text-gray-300">võrdlusperioodis andmed puuduvad</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-green-600' : 'text-red-500'}`}>
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {up ? '+' : ''}{pct.toFixed(0)}% <span className="text-gray-400 font-normal">{label}</span>
    </span>
  );
}

function pctOf(part: number, whole: number): string {
  if (!whole) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

// ── Inquiry aggregation ──────────────────────────────────────────────────────

interface InqAgg {
  count: number;
  byQuality: Record<InquiryQuality, number>;
  won: number;
  lost: number;
  offers: number;
  wonValue: number;
  convPct: number | null;
  /** Rows marked Not qualified — spam. Counted, but kept out of the funnel. */
  spam: number;
  bySource: { name: string; count: number; won: number }[];
  byCategory: { name: string; count: number; won: number }[];
  byAssignee: { id: string; count: number; won: number }[];
}

function aggregateInquiries(rows: Inquiry[], from: string, to: string): InqAgg {
  const inRange = rows.filter((r) => {
    const k = r.arrivedAt.split('T')[0].slice(0, 7);
    return k >= from && k <= to;
  });
  const byQuality = { unclear: 0, qualified: 0, not_qualified: 0, haldus: 0 } as Record<InquiryQuality, number>;
  for (const r of inRange) byQuality[r.quality] += 1;
  // Spam inflates the denominator and makes conversion look worse than it is,
  // so everything below counts real inquiries only.
  const spam = inRange.filter((r) => r.quality === 'not_qualified').length;
  const real = inRange.filter((r) => r.quality !== 'not_qualified');

  const cat = new Map<string, { count: number; won: number }>();
  const asg = new Map<string, { count: number; won: number }>();
  const src = new Map<string, { count: number; won: number }>();
  let won = 0, lost = 0, offers = 0, wonValue = 0;
  for (const r of real) {
    if (r.status === 'sai_kliendiks') { won++; wonValue += Number(r.value ?? 0); }
    if (r.status === 'ei_saanud') lost++;
    if (r.status === 'pakkumine_saadetud') offers++;
    const c = r.category || '— määramata';
    if (!cat.has(c)) cat.set(c, { count: 0, won: 0 });
    cat.get(c)!.count++;
    if (r.status === 'sai_kliendiks') cat.get(c)!.won++;
    const a = r.assigneeId ?? '';
    if (!asg.has(a)) asg.set(a, { count: 0, won: 0 });
    asg.get(a)!.count++;
    if (r.status === 'sai_kliendiks') asg.get(a)!.won++;
    const so = r.utmSource || '— teadmata';
    if (!src.has(so)) src.set(so, { count: 0, won: 0 });
    src.get(so)!.count++;
    if (r.status === 'sai_kliendiks') src.get(so)!.won++;
  }
  return {
    count: real.length,
    spam,
    bySource: Array.from(src.entries()).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.count - a.count),
    byQuality,
    won,
    lost,
    offers,
    wonValue,
    convPct: real.length ? (won / real.length) * 100 : null,
    byCategory: Array.from(cat.entries()).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.count - a.count),
    byAssignee: Array.from(asg.entries()).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.count - a.count),
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SalesStats() {
  const currentUser = useStore((s) => s.currentUser);
  const users = useStore((s) => s.users);
  const projects = useStore((s) => s.projects);
  const [entries, setEntries] = useState<SalesEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState<PeriodKey>('ytd');
  const [salesFilter, setSalesFilter] = useState('all');
  const [tab, setTab] = useState<'sales' | 'inquiries'>('sales');
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const now = new Date();
  const curKey = mk(now.getFullYear(), now.getMonth());
  const [customFrom, setCustomFrom] = useState(mk(now.getFullYear(), 0));
  const [customTo, setCustomTo] = useState(curKey);

  useEffect(() => {
    Promise.all([
      apiClient.get<{ entries: SalesEntry[] }>('/sales').then((r) => setEntries(r.entries)),
      apiClient.get<{ inquiries: Inquiry[] }>('/inquiries').then((r) => setInquiries(r.inquiries)),
    ])
      .catch((e) => setError((e as Error)?.message ?? 'Failed to load.'))
      .finally(() => setLoading(false));
  }, []);

  const canUse = currentUser && ['super_admin', 'admin', 'sales', 'sales_pm'].includes(currentUser.role);

  // Only people who actually sell: project sales owners plus anyone already
  // assigned on ledger rows. Excludes the seed "Admin" service account.
  const salespeople = useMemo(() => {
    const ids = new Set(projects.map((p) => p.salesOwnerId));
    for (const e of entries) if (e.salesPersonId) ids.add(e.salesPersonId);
    return users
      .filter((u) => ids.has(u.id) && !(u.name === 'Admin' || u.email === 'admin@vdisain.ee'))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, entries, users]);

  const filteredEntries = useMemo(
    () => (salesFilter === 'all' ? entries : entries.filter((e) => e.salesPersonId === salesFilter)),
    [entries, salesFilter],
  );

  const { cur, prev, range, growthLabel } = useMemo(() => {
    const r = ranges(period, customFrom, customTo);
    return {
      range: r,
      cur: aggregate(filteredEntries, r.from, r.to),
      prev: aggregate(filteredEntries, r.prevFrom, r.prevTo),
      growthLabel: PERIODS.find((p) => p.key === period)!.growthLabel,
    };
  }, [filteredEntries, period, customFrom, customTo]);

  // Inquiry assignees: salespeople only — project sales owners plus anyone
  // already assigned on an inquiry. Seed service account excluded.
  const assignees = useMemo(() => {
    const ids = new Set(projects.map((p) => p.salesOwnerId));
    for (const i of inquiries) if (i.assigneeId) ids.add(i.assigneeId);
    return users
      .filter((u) => ids.has(u.id) && !(u.name === 'Admin' || u.email === 'admin@vdisain.ee'))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, inquiries, users]);
  const assigneeName = (id: string) => (id ? assignees.find((u) => u.id === id)?.name ?? '?' : 'Määramata');

  const filteredInquiries = useMemo(
    () => (assigneeFilter === 'all' ? inquiries : inquiries.filter((i) => i.assigneeId === assigneeFilter)),
    [inquiries, assigneeFilter],
  );

  const { inqCur, inqPrev } = useMemo(() => {
    const r = ranges(period, customFrom, customTo);
    return {
      inqCur: aggregateInquiries(filteredInquiries, r.from, r.to),
      inqPrev: aggregateInquiries(filteredInquiries, r.prevFrom, r.prevTo),
    };
  }, [filteredInquiries, period, customFrom, customTo]);

  if (!canUse) {
    return <div className="p-8 text-center text-gray-400">This page is available to admins and salespeople only.</div>;
  }

  const cardCls = 'bg-white rounded-xl border border-gray-100 p-5';
  const monthInputCls = 'text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 text-gray-700 bg-white';

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Müük · Statistika</h1>
          <p className="text-gray-500 text-sm mt-1">Periood {range.from} – {range.to}</p>
        </div>
        <div className="flex items-center gap-2">
          <User size={14} className="text-gray-400 shrink-0" />
          {tab === 'sales' ? (
            <select
              value={salesFilter}
              onChange={(e) => setSalesFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 bg-white"
            >
              <option value="all">Kõik müügijuhid</option>
              {salespeople.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          ) : (
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 bg-white"
            >
              <option value="all">Kõik saajad</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-5 bg-white border border-gray-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setTab('sales')}
          className={`flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${
            tab === 'sales' ? 'bg-brand-500 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Banknote size={14} /> Müük
        </button>
        <button
          onClick={() => setTab('inquiries')}
          className={`flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${
            tab === 'inquiries' ? 'bg-brand-500 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Inbox size={14} /> Päringud
        </button>
      </div>

      {/* Period selector */}
      <div className="flex gap-1.5 flex-wrap items-center mb-6">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
              period === p.key
                ? 'border-brand-300 bg-brand-50 text-brand-600'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            {p.label}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-1.5 ml-1">
            <input type="month" value={customFrom} onChange={(e) => e.target.value && setCustomFrom(e.target.value)} className={monthInputCls} />
            <span className="text-xs text-gray-400">—</span>
            <input type="month" value={customTo} onChange={(e) => e.target.value && setCustomTo(e.target.value)} className={monthInputCls} />
          </div>
        )}
      </div>

      {error && <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 mb-4">{error}</div>}
      {loading && <div className="p-12 text-center text-gray-400 text-sm">Laen…</div>}

      {!loading && !error && tab === 'sales' && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Käive</p>
                <div className="p-2 bg-gray-100 rounded-lg"><Banknote size={15} className="text-gray-500" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(cur.revenue)}</p>
              <div className="mt-1"><GrowthChip pct={growthPct(cur.revenue, prev.revenue)} label={growthLabel} /></div>
            </div>

            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Disain</p>
                <div className="p-2 bg-purple-100 rounded-lg"><PieChart size={15} className="text-purple-600" /></div>
              </div>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(cur.design)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{pctOf(cur.design, cur.revenue)} käibest</p>
              <div className="mt-1"><GrowthChip pct={growthPct(cur.design, prev.design)} label={growthLabel} /></div>
            </div>

            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Arendus</p>
                <div className="p-2 bg-blue-100 rounded-lg"><PieChart size={15} className="text-blue-600" /></div>
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(cur.dev)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{pctOf(cur.dev, cur.revenue)} käibest</p>
              <div className="mt-1"><GrowthChip pct={growthPct(cur.dev, prev.dev)} label={growthLabel} /></div>
            </div>

            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Müüdud tunnid</p>
                <div className="p-2 bg-amber-100 rounded-lg"><Clock size={15} className="text-amber-600" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{Math.round(cur.hoursTotal)} h</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {Math.round(cur.hoursDesign)} h disain · {Math.round(cur.hoursDev)} h arendus
              </p>
              <div className="mt-1"><GrowthChip pct={growthPct(cur.hoursTotal, prev.hoursTotal)} label={growthLabel} /></div>
            </div>

            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Kliente</p>
                <div className="p-2 bg-green-100 rounded-lg"><Users size={15} className="text-green-600" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{cur.clientCount}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                <span className="text-green-600 font-medium">{cur.newClients} uut</span>
                {' · '}
                <span className="text-gray-600">{cur.returningClients} püsiklienti</span>
              </p>
              <div className="mt-1"><GrowthChip pct={growthPct(cur.clientCount, prev.clientCount)} label={growthLabel} /></div>
            </div>
          </div>

          {/* Average client block */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={cardCls}>
              <p className="text-xs font-medium text-gray-400 mb-2">Keskmine tehing</p>
              <p className="text-xl font-bold text-gray-900">{cur.avgDeal != null ? formatCurrency(cur.avgDeal) : '—'}</p>
              <p className="text-xs text-gray-400 mt-0.5">käive / klientide arv</p>
            </div>
            <div className={cardCls}>
              <p className="text-xs font-medium text-gray-400 mb-2">Keskmine klient · kokku</p>
              <p className="text-xl font-bold text-gray-900">{cur.avgClientTotal != null ? formatCurrency(cur.avgClientTotal) : '—'}</p>
            </div>
            <div className={cardCls}>
              <p className="text-xs font-medium text-gray-400 mb-2">Keskmine klient · disain</p>
              <p className="text-xl font-bold text-purple-600">{cur.avgClientDesign != null ? formatCurrency(cur.avgClientDesign) : '—'}</p>
            </div>
            <div className={cardCls}>
              <p className="text-xs font-medium text-gray-400 mb-2">Keskmine klient · arendus</p>
              <p className="text-xl font-bold text-blue-600">{cur.avgClientDev != null ? formatCurrency(cur.avgClientDev) : '—'}</p>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Keskmine klient: sama kliendi mitu rida perioodis summeeritakse enne keskmise arvutamist, et sagedased kliendid keskmist ei moonutaks.
            Müüdud tunnid: sisestatud tunnid, või summa / 70 € kui tunde pole logitud.
            {salesFilter !== 'all' && ' Müügijuhi filter arvestab ainult määratud müügijuhiga ridu.'}
          </p>
        </>
      )}

      {!loading && !error && tab === 'inquiries' && (
        <>
          {/* Inquiry summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Päringuid</p>
                <div className="p-2 bg-gray-100 rounded-lg"><Inbox size={15} className="text-gray-500" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{inqCur.count}</p>
              <div className="mt-1"><GrowthChip pct={growthPct(inqCur.count, inqPrev.count)} label={growthLabel} /></div>
            </div>

            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Konversioon</p>
                <div className="p-2 bg-green-100 rounded-lg"><Target size={15} className="text-green-600" /></div>
              </div>
              <p className="text-2xl font-bold text-green-600">{inqCur.convPct != null ? `${inqCur.convPct.toFixed(0)}%` : '—'}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {inqCur.won} sai kliendiks · {inqCur.offers} pakkumist väljas · {inqCur.lost} ei saanud
              </p>
              {inqCur.spam > 0 && (
                <p className="text-[11px] text-gray-300 mt-0.5">{inqCur.spam} rämpspäringut arvestusest välja jäetud</p>
              )}
              <p className="text-xs text-gray-300 mt-1">
                {inqPrev.convPct != null ? `eelmine periood: ${inqPrev.convPct.toFixed(0)}%` : 'võrdlusperioodis andmed puuduvad'}
              </p>
            </div>

            <div className={cardCls}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Võidetud käive</p>
                <div className="p-2 bg-green-100 rounded-lg"><Banknote size={15} className="text-green-600" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(inqCur.wonValue)}</p>
              <p className="text-xs text-gray-400 mt-0.5">võidetud päringute väärtus</p>
              <div className="mt-1"><GrowthChip pct={growthPct(inqCur.wonValue, inqPrev.wonValue)} label={growthLabel} /></div>
            </div>

            <div className={cardCls}>
              <p className="text-xs font-medium text-gray-400 mb-2">Kvaliteet</p>
              <div className="space-y-1">
                {(Object.keys(QUALITY_CONFIG) as InquiryQuality[]).map((q) => (
                  <div key={q} className="flex items-center justify-between">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${QUALITY_CONFIG[q].cls}`}>{QUALITY_CONFIG[q].label}</span>
                    <span className="text-xs font-semibold text-gray-700">
                      {inqCur.byQuality[q]}
                      <span className="text-gray-300 font-normal ml-1">{pctOf(inqCur.byQuality[q], inqCur.count)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lead sources — UTM on the website form */}
          <div className="bg-white rounded-xl border border-gray-100 mb-4">
            <div className="px-5 py-3 border-b border-gray-50 flex items-baseline justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-800">Kust tulid?</h3>
              <span className="text-xs text-gray-400">kontaktivormi allikas · rämps välja arvatud</span>
            </div>
            <div className="divide-y divide-gray-50 max-h-[320px] overflow-y-auto scrollbar-thin">
              {inqCur.bySource.length === 0 && <p className="px-5 py-6 text-center text-sm text-gray-300">Perioodis päringuid pole</p>}
              {inqCur.bySource.map((src) => (
                <div key={src.name} className="px-5 py-2 flex items-center gap-3">
                  <span className={`text-sm w-40 shrink-0 ${src.name.startsWith('—') ? 'text-gray-400 italic' : 'text-gray-700'}`}>{src.name}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-400 rounded-full"
                      style={{ width: `${inqCur.count ? Math.round((src.count / inqCur.count) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-32 text-right shrink-0">
                    <span className="font-semibold text-gray-800">{src.count}</span>
                    <span className="text-gray-300 ml-1">{pctOf(src.count, inqCur.count)}</span>
                    {src.won > 0 && <span className="text-green-600 ml-2">{src.won} võidetud</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Breakdown lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-100">
              <div className="px-5 py-3 border-b border-gray-50">
                <h3 className="text-sm font-semibold text-gray-800">Mida soovitakse?</h3>
              </div>
              <div className="divide-y divide-gray-50 max-h-[320px] overflow-y-auto scrollbar-thin">
                {inqCur.byCategory.length === 0 && <p className="px-5 py-6 text-center text-sm text-gray-300">Perioodis päringuid pole</p>}
                {inqCur.byCategory.map((c) => (
                  <div key={c.name} className="px-5 py-2 flex items-center justify-between">
                    <span className="text-sm text-gray-700">{c.name}</span>
                    <span className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-800">{c.count}</span>
                      {c.won > 0 && <span className="text-green-600 ml-2">{c.won} võidetud</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100">
              <div className="px-5 py-3 border-b border-gray-50">
                <h3 className="text-sm font-semibold text-gray-800">Kes sai endale?</h3>
              </div>
              <div className="divide-y divide-gray-50 max-h-[320px] overflow-y-auto scrollbar-thin">
                {inqCur.byAssignee.length === 0 && <p className="px-5 py-6 text-center text-sm text-gray-300">Perioodis päringuid pole</p>}
                {inqCur.byAssignee.map((a) => (
                  <div key={a.id || 'none'} className="px-5 py-2 flex items-center justify-between">
                    <span className={`text-sm ${a.id ? 'text-gray-700' : 'text-gray-400 italic'}`}>{assigneeName(a.id)}</span>
                    <span className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-800">{a.count}</span>
                      {a.won > 0 && <span className="text-green-600 ml-2">{a.won} võidetud</span>}
                      {a.count > 0 && <span className="text-gray-300 ml-2">{Math.round((a.won / a.count) * 100)}%</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Konversioon = „{STATUS_CONFIG.sai_kliendiks.label}" staatusega päringud / kõik perioodi päringud (saabumise kuupäeva järgi).
            Võidetud käive tuleb päringu Väärtus (€) väljast.
          </p>
        </>
      )}
    </div>
  );
}
